'use client';

import type { ReviewSectionProps } from './ReviewSection.types';
import {
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  Pill,
  Details,
  Text,
  InlineUnsafe,
  Separator,
} from '@/_components';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { getSectionStatus } from '@/_utils/sections';
import { getQuestionsForSection } from '@/_utils/questions';
import { useContext, useMemo, type FC } from 'react';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { SectionsSwitch } from '../SectionsSwitch';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { Question, Questionnaire } from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';

export const ReviewSection: FC<ReviewSectionProps> = ({
  mode,
  instruction,
}) => {
  const {
    isDisabled,
    selectedSections,
    selectedQuestions,
    selectedQuestionnaire,
    setReviewed,
    isReviewed,
    createDepositCheckoutSession,
  } = useContext(ClientQuestionnaireContext);
  const { previewSections, previewQuestions, previewQuestionnaire } =
    useContext(PreviewContext);
  const { previewTemplate, previewTemplateSections } =
    useContext(UserPreviewContext);
  const { preferences } = useContext(FirmSettingsContext);

  const questionnaire: Questionnaire | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestionnaire;
    if (mode === 'admin-template') return previewTemplate as any;

    return selectedQuestionnaire;
  }, [mode, previewTemplate, previewQuestionnaire, selectedQuestionnaire]);

  const sections: Array<Section> = useMemo(() => {
    if (mode === 'admin-preview') return previewSections;
    if (mode === 'admin-template') return previewTemplateSections;

    return selectedSections;
  }, [mode, previewSections, selectedSections, previewTemplateSections]);

  const questions: Array<Question> = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestions;
    if (mode === 'admin-template') {
      return previewTemplate?.questionnaire_questions || [];
    }

    return selectedQuestions;
  }, [
    mode,
    previewQuestions,
    previewTemplate?.questionnaire_questions,
    selectedQuestions,
  ]);

  const depositStatus: null | string = questionnaire?.deposit_status || null;

  const hasOutstandingBalance: boolean = Boolean(
    depositStatus &&
      depositStatus !== 'paid' &&
      depositStatus !== 'marked_paid' &&
      depositStatus !== 'payment_pending',
  );

  const reviewableSections: Array<Section> = sections.filter(
    (s) => s.section_type !== 4,
  );

  const toggleCheckbox = () => setReviewed(!isReviewed);

  return (
    <>
      {instruction && (
        <Flex isColumn={true} gap={12} padding={12}>
          <>
            <Text type="div">
              <InlineUnsafe text={instruction} isExtremelyUnsafe={true} />
            </Text>
            <Separator isLight={true} />
          </>
        </Flex>
      )}
      <Flex padding={12} gap={4} isColumn={true}>
        {!!reviewableSections?.length && (
          <Flex isColumn={true} gap={4}>
            {reviewableSections?.map((section, key) => {
              const sectionQuestions: Array<Question> = questions?.filter(
                (q) => q.section_id === section.id,
              );

              const hasMultipleSections: boolean = Boolean(
                sections?.length ?? 0 > 1,
              );

              const filteredSectionQuestions = getQuestionsForSection(
                questions,
                section?.section_type,
                questionnaire,
                hasMultipleSections &&
                  preferences?.hide_document_uploads_within_sections,
              );

              const status = getSectionStatus(
                filteredSectionQuestions,
                section,
              );

              return (
                <Details
                  title={section?.name}
                  key={key}
                  pills={[
                    {
                      label:
                        status === 'completed' ? 'Completed' : 'Incomplete',
                      intent: status === 'completed' ? 'success' : 'danger',
                    },
                  ]}
                >
                  <SectionsSwitch
                    mode={mode}
                    isReviewSection={true}
                    section={section}
                    questions={sectionQuestions}
                  />
                </Details>
              );
            })}
          </Flex>
        )}
        {!!questionnaire?.deposit_amount && (
          <Alert
            type={
              depositStatus === 'paid' || depositStatus === 'marked_paid'
                ? 'success'
                : depositStatus === 'payment_pending'
                  ? 'info'
                  : 'danger'
            }
            title={
              depositStatus === 'paid'
                ? 'Deposit has been successfully paid'
                : depositStatus === 'marked_paid'
                  ? 'Deposit marked as paid'
                  : depositStatus === 'payment_pending'
                    ? 'Your payment is awaiting processing, which might take a few days. Please check back later.'
                    : 'Outstanding balance'
            }
            actions={
              <>
                {depositStatus === 'paid' || depositStatus === 'marked_paid' ? (
                  <Flex>
                    <Pill
                      intent="success"
                      size="large"
                      label="Paid"
                      iconLeft="check-circle"
                    />
                  </Flex>
                ) : depositStatus === 'payment_pending' ? (
                  <Flex>
                    <Pill
                      intent="info"
                      size="large"
                      label="Pending"
                      iconLeft="spinner-gap"
                    />
                  </Flex>
                ) : (
                  <ButtonGroup align="center">
                    <Button
                      intent="danger"
                      onClick={createDepositCheckoutSession}
                      isDisabled={mode !== 'client'}
                      value={`Pay deposit ($${questionnaire?.deposit_amount})`}
                    />
                  </ButtonGroup>
                )}
              </>
            }
          >
            {hasOutstandingBalance &&
              'You have an outstanding balance to be paid before you can complete the Questionnaire.'}
          </Alert>
        )}
        <Alert
          type="info"
          title="By submitting the questionnaire:"
          actions={
            <Flex isCentered={true}>
              <Checkbox
                isChecked={isReviewed}
                useCustomTheme={true}
                onChange={toggleCheckbox}
                isDisabled={isDisabled || hasOutstandingBalance}
                label="I Agree"
              />
            </Flex>
          }
        >
          {questionnaire?.disclaimer}
        </Alert>
      </Flex>
    </>
  );
};
